<template>
  <div class="actions">
    <button
      v-for="button in buttons"
      :key="button.text"
      :class="button.class"
      :name="button.name || button.icon"
      @click="button.click()">
      <icon-add v-if="button.icon === 'add'" />
      <icon-delete v-if="button.icon === 'delete'" />
      <icon-ungroup v-if="button.icon === 'ungroup'" />
      {{ button.text }}
    </button>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import { AudienceRuleButtons } from '@/common/enums/audience-rule-buttons.enum';
  import { AudienceRuleModes } from '@/common/enums/audience-rule-modes';
  import { DataMarketplaceCodes } from '@/common/enums/data-marketplace-codes.enum';
  import { DataProviderCodes, isLifesightDataProvider } from '@/common/enums/data-provider-codes';
  import { IconAdd, IconDelete, IconUngroup } from '@/common/icons/monochrome';
  import { AudienceLogic } from '@/common/models';
  import { useInjected } from '@/globals';
  import { useDataMarketplaces, useDataProviders } from '@/org-app/services/tanstack/queries';
  import { mapActions } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  // Setup
  const props = withDefaults(
    defineProps<{
      buttonSize: string;
      showButtonsOnly: AudienceRuleButtons[];
    }>(),
    {
      buttonSize: 'btn-sm',
      showButtonsOnly: () => [],
    },
  );
  const emits = defineEmits<{
    (e: 'deleteGroup');
    (e: 'removeGroup');
    (e: 'addGroup');
    (e: 'addRule', mode: AudienceRuleModes);
  }>();
  const audienceLogic = useInjected<AudienceLogic>('audienceLogic');
  const { updateAudienceLogicDataProviderID, updateAudienceLogicDataMarketplaceID } = mapActions();
  const { items: dataMarketplaces } = useDataMarketplaces();
  const { items: dataProviders } = useDataProviders();

  // Computed
  const btnDanger = computed(() => `btn btn-danger ${props.buttonSize}`);
  const btnDefault = computed(() => `btn btn-default ${props.buttonSize}`);
  const buttons = computed(() => {
    const buttons = [];

    if (props.showButtonsOnly.includes(AudienceRuleButtons.Delete)) {
      buttons.push({
        class: btnDanger.value,
        click: () => emits('deleteGroup'),
        icon: 'delete',
        name: '',
        text: i18n.global.t('actions.delete'),
      });
    }
    if (props.showButtonsOnly.includes(AudienceRuleButtons.Ungroup)) {
      buttons.push({
        class: btnDanger.value,
        click: () => emits('removeGroup'),
        icon: 'ungroup',
        name: 'ungroup',
        text: i18n.global.t('actions.ungroup'),
      });
    }
    if (props.showButtonsOnly.includes(AudienceRuleButtons.Epsilon)) {
      buttons.push({
        class: btnDefault.value,
        click: addEpsilonRule,
        icon: 'add',
        name: 'addEpsilonSegmentRule',
        text: i18n.global.t('actions.addEpsilonSegmentRule'),
      });
    }
    if (props.showButtonsOnly.includes(AudienceRuleButtons.Adsquare)) {
      buttons.push({
        class: btnDefault.value,
        click: addAdsquareRule,
        icon: 'add',
        name: 'addAdsquareSegmentRule',
        text: i18n.global.t('actions.addAdsquareSegmentRule'),
      });
    }
    if (props.showButtonsOnly.includes(AudienceRuleButtons.Mastercard)) {
      buttons.push({
        class: btnDefault.value,
        click: addMastercardRule,
        icon: 'add',
        name: 'addMastercardSegmentRule',
        text: i18n.global.t('actions.addMastercardSegmentRule'),
      });
    }
    if (props.showButtonsOnly.includes(AudienceRuleButtons.Liveramp)) {
      buttons.push({
        class: btnDefault.value,
        click: addLiverampRule,
        icon: 'add',
        name: 'addLiverampSegmentRule',
        text: i18n.global.t('actions.addLiverampSegmentRule'),
      });
    }
    if (props.showButtonsOnly.includes(AudienceRuleButtons.Predefined)) {
      buttons.push({
        class: btnDefault.value,
        click: addLifesightRule,
        icon: 'add',
        name: 'addLifesightSegmentRule',
        text: i18n.global.t('actions.addPredefinedSegmentRule'),
      });
    }
    if (props.showButtonsOnly.includes(AudienceRuleButtons.Geofence)) {
      buttons.push({
        class: btnDefault.value,
        click: () => emits('addRule', AudienceRuleModes.Geofence),
        icon: 'add',
        name: 'addGeofenceGroupRule',
        text: i18n.global.t('actions.addRule'),
      });
    }
    if (props.showButtonsOnly.includes(AudienceRuleButtons.Group)) {
      buttons.push({
        class: btnDefault.value,
        click: () => emits('addGroup'),
        icon: 'add',
        name: 'addGroup',
        text: i18n.global.t('actions.addGroup'),
      });
    }
    if (props.showButtonsOnly.includes(AudienceRuleButtons.Demographic)) {
      buttons.push({
        class: btnDefault.value,
        click: () => emits('addRule', AudienceRuleModes.PredefinedDemographic),
        icon: 'add',
        name: 'addDemographicsRules',
        text: i18n.global.t('actions.addPredefinedDemographicRules'),
      });
    }
    if (props.showButtonsOnly.includes(AudienceRuleButtons.DeviceID)) {
      buttons.push({
        class: btnDefault.value,
        click: () => emits('addRule', AudienceRuleModes.Demographic),
        icon: 'add',
        name: 'addAudienceDeviceIDGroup',
        text: i18n.global.t('actions.addDeviceIDSegmentRule'),
      });
    }
    if (props.showButtonsOnly.includes(AudienceRuleButtons.Demo)) {
      buttons.push({
        class: btnDefault.value,
        click: () => emits('addRule', AudienceRuleModes.Demographic),
        icon: 'add',
        name: '',
        text: i18n.global.t('actions.addRuleDemo'),
      });
    }

    return buttons;
  });

  function addEpsilonRule() {
    updateAudienceLogicDataProviderID({
      audienceLogic: audienceLogic.value,
      value: dataProviders.value.find((dp) => dp.code === DataProviderCodes.EPSILON)?.id,
    });
    emits('addRule', AudienceRuleModes.Epsilon);
  }
  function addLifesightRule() {
    updateAudienceLogicDataProviderID({
      audienceLogic: audienceLogic.value,
      value: dataProviders.value.find(isLifesightDataProvider)?.id,
    });
    emits('addRule', AudienceRuleModes.Segments);
  }
  function addLiverampRule() {
    updateAudienceLogicDataMarketplaceID({
      audienceLogic: audienceLogic.value,
      value: dataMarketplaces.value.find((dm) => dm.code === DataMarketplaceCodes.LIVERAMP)?.id,
    });
    emits('addRule', AudienceRuleModes.Segments);
  }
  function addAdsquareRule() {
    updateAudienceLogicDataProviderID({
      audienceLogic: audienceLogic.value,
      value: dataProviders.value.find((dp) => dp.code === DataProviderCodes.ADSQUARE)?.id,
    });
    emits('addRule', AudienceRuleModes.Segments);
  }
  function addMastercardRule() {
    updateAudienceLogicDataProviderID({
      audienceLogic: audienceLogic.value,
      value: dataProviders.value.find((dp) => dp.code === DataProviderCodes.MASTERCARD)?.id,
    });
    emits('addRule', AudienceRuleModes.Segments);
  }
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  .actions {
    display: flex;
    flex-grow: 1;
    flex-shrink: 1;
    flex-direction: row-reverse;
    flex-wrap: wrap !important;
    align-items: center;
    gap: 1rem;

    .toggle {
      margin-right: 1.6rem;
    }
  }
</style>
